import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Dropdown from 'react-bootstrap/Dropdown'
import InputGroup from 'react-bootstrap/InputGroup'
import Description from '../../ui-helper-elements/Description'
import { useQuoteContext } from '../../../hooks/useQuoteContext'
import useFeaturePermissions from '../../../hooks/useFeaturePermissions'
import { DealDeskDiscount } from '../../../types/types'
import { generateUUID } from '../../../utils/dealDeskDiscountConverter'

interface DealDeskDiscountCreatorButtonProps {
   buttonClass?: string
   buttonSize?: 'sm' | 'lg' | undefined
}

interface DiscountFormRow {
   temp_id: string
   priority: number | ''
   discount_amount: number | ''
   discount_type: 'percent' | 'fixed' | 'flatrate'
   billing_freq: 'monthly' | 'once'
   apply_to: Set<string>
   start_month: number | null
   end_month: number | null
   applies_to_commission: boolean
   commission_discount_amount: number | null
   disclaimer: string
   apply_to_pdf_displayed_value: boolean
}

export default function DealDeskDiscountCreatorButton(
   props: DealDeskDiscountCreatorButtonProps,
) {
   const [show, setShow] = useState(false)
   const [discountRows, setDiscountRows] = useState<DiscountFormRow[]>([])
   const [showConsultingServices, setShowConsultingServices] = useState<{
      [key: string]: boolean
   }>({})
   const [invalidRows, setInvalidRows] = useState<Set<string>>(new Set())
   const [invalidCommissionRows, setInvalidCommissionRows] = useState<
      Set<string>
   >(new Set())
   const state = useQuoteContext()
   const { getString } = state
   const { hasPermission } = useFeaturePermissions()

   // Check feature permission
   const canAccessFeature = hasPermission(
      'DealDeskDiscountBuilder',
      state.user_role,
   )

   // Generate product lists dynamically from product_list based on BillingFreq
   // Returns array of objects with productCode and productName
   const getProductsByBillingFreq = (
      billingFreq: 'monthly' | 'once',
      tempId: string,
   ) => {
      if (!state.product_list) return []

      const includeExtendedProducts =
         billingFreq === 'monthly' || showConsultingServices[tempId]

      // Product families to hide when extended products checkbox is unchecked
      const extendedProductFamilies = [
         'Consulting Services',
         'TimeClocks',
         'Time Clocks Shipping',
         'Professional Services',
      ]

      const productsToNeverShow = new Set(['whatsThis'])

      // Get selected Core and Implementation products from state
      const selectedCore = state.coreProductSelected

      // Determine selected HR implementation based on impType
      let selectedImplementation: string | null = null
      if (state.impType === 'premier') {
         selectedImplementation = `${selectedCore}PremierImp`
      } else if (state.impType === 'quickstart') {
         selectedImplementation = `${selectedCore}Quickstart`
      } else if (state.impType === 'full') {
         selectedImplementation = `${selectedCore}Imp`
      } else if (state.impType === 'selfSetup') {
         selectedImplementation = null // No implementation product for self-setup
      }

      // Determine selected Payroll implementation based on payrollImpType
      let selectedPayrollImplementation: string | null = null
      if (state.payrollImpType === 'full') {
         selectedPayrollImplementation = 'payrollImp'
      } else if (state.payrollImpType === 'foundations') {
         selectedPayrollImplementation = 'payrollFoundationsImp'
      }

      return Object.keys(state.product_list)
         .filter((productCode) => {
            const product = state.product_list[productCode]
            // Match 'monthly' or 'once' billing frequency
            if (product?.BillingFreq !== billingFreq) return false

            // Filter out extended product families if checkbox is unchecked for one-time products
            if (
               !includeExtendedProducts &&
               extendedProductFamilies.includes(product?.ProductFamily || '')
            ) {
               return false
            }

            // Filter HR Core family to only show the selected core product
            if (product?.ProductFamily === 'HR Core') {
               if (!selectedCore || productCode !== selectedCore) {
                  return false
               }
            }

            // Filter HR Implementation family
            if (product?.ProductFamily === 'HR implementation') {
               // If selfSetup, hide all HR implementation products
               if (state.impType === 'selfSetup') {
                  return false
               }
               // If extended products is checked, show all implementation products
               if (includeExtendedProducts) {
                  return true
               }
               // Otherwise, only show the selected implementation
               if (
                  !selectedImplementation ||
                  productCode !== selectedImplementation
               ) {
                  return false
               }
            }

            // Filter Payroll Implementation family
            if (product?.ProductFamily === 'Payroll Implementation') {
               // If extended products is checked, show all payroll implementation products
               if (includeExtendedProducts) {
                  return true
               }
               // Otherwise, only show the selected payroll implementation
               if (
                  !selectedPayrollImplementation ||
                  productCode !== selectedPayrollImplementation
               ) {
                  return false
               }
            }

            // Filter out products that should never be shown
            if (productsToNeverShow.has(productCode)) {
               return false
            }

            return true
         })
         .map((productCode) => {
            const product = state.product_list[productCode]
            let displayName = product?.ProductName || productCode

            // Override display name for HR implementation products
            if (product?.ProductFamily === 'HR implementation') {
               // Parse the product code to determine implementation type and core level
               // Expected formats: core1Imp, core2PremierImp, core3Quickstart, etc.
               let impType = ''
               let coreLevel = ''

               // Determine implementation type from product code
               if (productCode.includes('PremierImp')) {
                  impType = 'Premier Implementation'
               } else if (productCode.includes('Quickstart')) {
                  impType = 'QuickStart'
               } else if (productCode.endsWith('Imp')) {
                  impType = 'HR Implementation'
               }

               // Determine core level from product code
               if (productCode.startsWith('core1')) {
                  coreLevel = 'Core'
               } else if (productCode.startsWith('core2')) {
                  coreLevel = 'Pro'
               } else if (productCode.startsWith('core3')) {
                  coreLevel = 'Elite'
               }

               if (impType && coreLevel) {
                  displayName = `${impType} - ${coreLevel}`
               }
            }

            // Override display name for Payroll implementation products
            if (product?.ProductFamily === 'Payroll Implementation') {
               // Expected formats: payrollImp, payrollFoundationsImp
               if (productCode === 'payrollImp') {
                  displayName = 'Payroll Implementation - Full'
               } else if (productCode === 'payrollFoundationsImp') {
                  displayName = 'Payroll Implementation - Foundations'
               }
            }

            // Prepend product family name for Consulting Services
            if (product?.ProductFamily === 'Consulting Services') {
               displayName = `Consulting Service - ${displayName}`
            }

            // Prepend product family name for Professional Services
            if (product?.ProductFamily === 'Professional Services') {
               displayName = `Professional Service - ${displayName}`
            }

            return {
               code: productCode,
               name: displayName,
            }
         })
         .sort((a, b) => a.name.localeCompare(b.name)) // Sort by name alphabetically
   }

   const handleClose = () => {
      setShow(false)
      setDiscountRows([]) // Clear rows on close
   }

   const handleShow = () => {
      setShow(true)
      // Load existing discounts from context (snake_case) and convert to form rows
      const existingDiscounts: DiscountFormRow[] =
         state.dealDeskDiscountRecords.map(
            (discount): DiscountFormRow => ({
               temp_id: discount.id,
               priority: discount.priority === 0 ? '' : discount.priority,
               discount_amount:
                  discount.discount_amount === 0
                     ? ''
                     : discount.discount_amount,
               discount_type: discount.discount_type,
               billing_freq: discount.billing_freq,
               apply_to: new Set(discount.apply_to || []), // Convert array to Set
               start_month: discount.start_month,
               end_month: discount.end_month,
               applies_to_commission: discount.applies_to_commission,
               commission_discount_amount: discount.commission_discount_amount,
               disclaimer: discount.disclaimer ?? '',
               apply_to_pdf_displayed_value:
                  discount.apply_to_pdf_displayed_value ?? true, // Default to true
            }),
         )
      setDiscountRows(existingDiscounts)

      // Validate existing discounts
      const invalid = new Set<string>()
      existingDiscounts.forEach((row) => {
         if (row.apply_to.size === 0) {
            invalid.add(row.temp_id)
         }
      })
      setInvalidRows(invalid)

      // Initialize showConsultingServices state based on existing selections
      const consultingState: { [key: string]: boolean } = {}
      const extendedProductFamilies = [
         'Consulting Services',
         'Time Clocks',
         'Time Clocks Shipping',
      ]
      existingDiscounts.forEach((discount) => {
         if (discount.billing_freq === 'once') {
            const hasExtendedProduct = Array.from(discount.apply_to).some(
               (code: string) =>
                  extendedProductFamilies.includes(
                     state.product_list[code]?.ProductFamily || '',
                  ),
            )
            consultingState[discount.temp_id] = hasExtendedProduct
         }
      })
      setShowConsultingServices(consultingState)
   }

   const handleAddDiscount = () => {
      // Get default disclaimer from string table
      const defaultDisclaimer = getString(
         'DealDeskDiscount.DefaultDisclaimer',
         '$DISCOUNT_PERCENT discount on $APPLIED_PRODUCTS for Months $START_MONTH to $END_MONTH. After the discount ends, your monthly cost will be $TOTAL_WITHOUT_DISCOUNT.',
      )
      const newRow: DiscountFormRow = {
         temp_id: `temp-${generateUUID()}`,
         priority: '',
         discount_amount: '',
         discount_type: 'percent',
         billing_freq: 'monthly', // Default to monthly
         apply_to: new Set<string>(),
         start_month: null,
         end_month: null,
         applies_to_commission: true, // Default to true for monthly billing
         commission_discount_amount: null,
         disclaimer: defaultDisclaimer,
         apply_to_pdf_displayed_value: true, // Default to true
      }
      setDiscountRows([...discountRows, newRow])
      // Mark new row as invalid since apply_to is empty
      setInvalidRows(new Set([...invalidRows, newRow.temp_id]))
   }

   const getFilteredProducts = (
      billingFreq: 'monthly' | 'once',
      tempId: string,
   ) => {
      return getProductsByBillingFreq(billingFreq, tempId)
   }

   const handleRemoveDiscount = (temp_id: string) => {
      setDiscountRows(discountRows.filter((row) => row.temp_id !== temp_id))
      // Remove from invalid rows if present
      if (invalidRows.has(temp_id)) {
         const newInvalidRows = new Set(invalidRows)
         newInvalidRows.delete(temp_id)
         setInvalidRows(newInvalidRows)
      }
   }

   const handleFieldChange = (
      temp_id: string,
      field: keyof DiscountFormRow,
      value: any,
   ) => {
      setDiscountRows(
         discountRows.map((row) =>
            row.temp_id === temp_id ? { ...row, [field]: value } : row,
         ),
      )
   }

   const handleApplyToChange = (
      temp_id: string,
      selectedOptions: Set<string>,
   ) => {
      handleFieldChange(temp_id, 'apply_to', selectedOptions)
      // Clear validation error when user selects products
      if (selectedOptions.size > 0 && invalidRows.has(temp_id)) {
         const newInvalidRows = new Set(invalidRows)
         newInvalidRows.delete(temp_id)
         setInvalidRows(newInvalidRows)
      }
   }

   // Don't render if user doesn't have permission
   if (!canAccessFeature) {
      return null
   }

   /**
    * Calculate the default commission discount amount (always a percentage)
    * This represents the commissionable percentage of MRR after accounting for the discount
    *
    * Formula with month range: (12 - (discountAmount/100 * (endMonth - startMonth + 1))) / 12 * 100
    * This calculates: what % of the year's MRR is commissionable after a partial-year discount
    * Example: 50% discount for months 1-3 = (12 - (0.5 * 3)) / 12 * 100 = 87.5% commissionable
    *
    * Formula without month range: 100 - discountAmount
    * This is a full-year discount, so commissionable = 100% - discount%
    * Example: 20% discount all year = 80% commissionable
    *
    * Only applies to percent discount types
    */
   const calculateDefaultCommissionAmount = (
      discountAmount: number,
      discountType: 'percent' | 'fixed' | 'flatrate',
      startMonth: number | null,
      endMonth: number | null,
   ): number | null => {
      // Only calculate for percent discounts
      if (discountType !== 'percent') {
         return null
      }

      if (startMonth !== null && endMonth !== null) {
         // Formula: (12 - (discountAmount/100 * (endMonth - startMonth + 1))) / 12 * 100
         const monthCount = endMonth - startMonth + 1
         const result = ((12 - (discountAmount / 100) * monthCount) / 12) * 100
         // Round to 5 decimal places
         return Math.round(result * 100000) / 100000
      } else {
         // No month range: 100 - discountAmount
         return 100 - discountAmount
      }
   }

   const validateRows = () => {
      const invalid = new Set<string>()
      const invalidCommission = new Set<string>()
      discountRows.forEach((row) => {
         if (row.apply_to.size === 0) {
            invalid.add(row.temp_id)
         }
         // Check commission discount amount when applies_to_commission is checked
         if (row.applies_to_commission) {
            if (
               row.commission_discount_amount === null ||
               row.commission_discount_amount === undefined ||
               row.commission_discount_amount <= 0
            ) {
               invalidCommission.add(row.temp_id)
            }
         }
      })
      setInvalidRows(invalid)
      setInvalidCommissionRows(invalidCommission)
      return invalid.size === 0 && invalidCommission.size === 0
   }

   const handleSubmit = async (event: React.FormEvent) => {
      event.preventDefault()

      // Validate all rows
      if (!validateRows()) {
         return
      }

      // TODO: Implement API call to save discounts
      // For now, just update the context
      // Save discounts with snake_case properties to match API/database format
      const discountsToSave: DealDeskDiscount[] = discountRows.map((row) => {
         // Generate default name based on billing frequency
         const discountName =
            row.billing_freq === 'monthly'
               ? 'Additional Monthly Discount'
               : 'Additional One Time Discount'

         return {
            // Keep temp IDs for new discounts - server will generate real UUIDs on save
            // Temp IDs are filtered out of promotionalDiscounts before saving to backend
            id: row.temp_id,
            priority: row.priority === '' ? 0 : row.priority,
            discount_amount:
               row.discount_amount === '' ? 0 : row.discount_amount,
            discount_type: row.discount_type,
            billing_freq: row.billing_freq,
            apply_to: Array.from(row.apply_to), // Convert Set to array
            // Set start_month and end_month to null for one-time discounts
            start_month: row.billing_freq === 'once' ? null : row.start_month,
            end_month: row.billing_freq === 'once' ? null : row.end_month,
            applies_to_commission: row.applies_to_commission,
            // Save null for commission_discount_amount if applies_to_commission is false
            commission_discount_amount: row.applies_to_commission
               ? row.commission_discount_amount
               : null,
            // Save disclaimer, or null if empty
            disclaimer: row.disclaimer.trim() || null,
            // Save apply_to_pdf_displayed_value
            apply_to_pdf_displayed_value: row.apply_to_pdf_displayed_value,
         }
      })

      // Get the IDs of discounts being saved
      const newDiscountIds = new Set(discountsToSave.map((d) => d.id))

      // Get the IDs of discounts that existed before (to find removed ones)
      const previousDiscountIds = new Set(
         state.dealDeskDiscountRecords.map((d) => d.id),
      )

      // Find IDs that were removed (in previous but not in new)
      const removedIds = [...previousDiscountIds].filter(
         (id) => !newDiscountIds.has(id),
      )

      // Update promotionalDiscounts: add new IDs, remove deleted IDs
      state.setPromotionalDiscounts((prev) => {
         const updated = { ...prev }

         // Add all current discount IDs
         newDiscountIds.forEach((id) => {
            updated[id] = true
         })

         // Remove deleted discount IDs
         removedIds.forEach((id) => {
            delete updated[id]
         })

         return updated
      })

      // Update deal desk discount records in context
      // This will trigger the useEffect in App.tsx to sync with discount_list
      // Deal desk discounts are NOT saved to backend until the entire opportunity is saved
      state.setDealDeskDiscountRecords(discountsToSave)

      handleClose()
   }

   return (
      <>
         <Button
            size={props.buttonSize}
            className={props.buttonClass}
            onClick={handleShow}
            variant='primary'
         >
            {getString(
               'product.BottomToolbar.DealDeskDiscountButton',
               'Deal Desk Builder',
            )}
         </Button>

         <Modal
            show={show}
            onHide={handleClose}
            size='xl'
            aria-labelledby='deal-desk-discount-modal-title'
            centered
            scrollable
         >
            <Modal.Header closeButton>
               <Modal.Title id='deal-desk-discount-modal-title'>
                  {getString(
                     'product.DealDeskDiscountModal.Title',
                     'Deal Desk Discount Builder',
                  )}
               </Modal.Title>
            </Modal.Header>
            <Modal.Body>
               <Form onSubmit={handleSubmit}>
                  <div className='mb-3'>
                     <Button
                        variant='success'
                        size='sm'
                        onClick={handleAddDiscount}
                     >
                        + Add Discount
                     </Button>
                  </div>

                  {discountRows.length === 0 && (
                     <p className='text-muted'>
                        No discounts added yet. Click "Add Discount" to create
                        one.
                     </p>
                  )}

                  {discountRows.map((row, index) => (
                     <div
                        key={row.temp_id}
                        className='border rounded p-3 mb-3 bg-light'
                     >
                        <Row className='mb-2'>
                           <Col>
                              <h6>Discount #{index + 1}</h6>
                           </Col>
                           <Col xs='auto'>
                              <Button
                                 variant='danger'
                                 size='sm'
                                 onClick={() =>
                                    handleRemoveDiscount(row.temp_id)
                                 }
                              >
                                 Remove
                              </Button>
                           </Col>
                        </Row>

                        <Row className='mb-2'>
                           <Col md={4}>
                              <Form.Group>
                                 <Form.Label>Priority</Form.Label>
                                 <Form.Control
                                    type='number'
                                    step='1'
                                    value={
                                       row.priority === '' ? '' : row.priority
                                    }
                                    onChange={(e) =>
                                       handleFieldChange(
                                          row.temp_id,
                                          'priority',
                                          e.target.value === ''
                                             ? ''
                                             : parseInt(e.target.value) || 0,
                                       )
                                    }
                                    placeholder='0'
                                    required
                                 />
                              </Form.Group>
                           </Col>
                           <Col md={4}>
                              <Form.Group>
                                 <Form.Label>Discount Amount</Form.Label>
                                 <InputGroup>
                                    {(row.discount_type === 'fixed' ||
                                       row.discount_type === 'flatrate') && (
                                       <InputGroup.Text>
                                          {state.currencyObject?.Symbol || '$'}
                                       </InputGroup.Text>
                                    )}
                                    <Form.Control
                                       type='number'
                                       step='0.01'
                                       value={
                                          row.discount_amount === ''
                                             ? ''
                                             : row.discount_amount
                                       }
                                       onChange={(e) => {
                                          if (e.target.value === '') {
                                             handleFieldChange(
                                                row.temp_id,
                                                'discount_amount',
                                                '',
                                             )
                                          } else {
                                             const value = e.target.value
                                             // Check decimal places based on discount type
                                             const maxDecimals =
                                                row.discount_type === 'percent'
                                                   ? 5
                                                   : 2
                                             const decimalMatch =
                                                value.match(/\.\d+$/)
                                             if (
                                                decimalMatch &&
                                                decimalMatch[0].length - 1 >
                                                   maxDecimals
                                             ) {
                                                return // Prevent input if too many decimals
                                             }
                                             const newAmount =
                                                parseFloat(value) || 0
                                             // Update discount amount and recalculate commission if applicable
                                             setDiscountRows(
                                                discountRows.map((r) => {
                                                   if (
                                                      r.temp_id !== row.temp_id
                                                   )
                                                      return r
                                                   const updates: Partial<DiscountFormRow> =
                                                      {
                                                         discount_amount:
                                                            newAmount,
                                                      }
                                                   // Recalculate commission amount if applies_to_commission is checked and discount type is percent
                                                   if (
                                                      r.applies_to_commission &&
                                                      r.discount_type ===
                                                         'percent'
                                                   ) {
                                                      updates.commission_discount_amount =
                                                         calculateDefaultCommissionAmount(
                                                            newAmount,
                                                            r.discount_type,
                                                            r.start_month,
                                                            r.end_month,
                                                         )
                                                   }
                                                   return { ...r, ...updates }
                                                }),
                                             )
                                          }
                                       }}
                                       placeholder='0'
                                       required
                                    />
                                    {row.discount_type === 'percent' && (
                                       <InputGroup.Text>%</InputGroup.Text>
                                    )}
                                 </InputGroup>
                              </Form.Group>
                           </Col>
                        </Row>

                        <Row className='mb-2'>
                           <Col md={4}>
                              <Form.Group>
                                 <Form.Label>Discount Type</Form.Label>
                                 <Form.Select
                                    value={row.discount_type}
                                    onChange={(e) => {
                                       const newType = e.target.value as
                                          | 'percent'
                                          | 'fixed'
                                          | 'flatrate'
                                       handleFieldChange(
                                          row.temp_id,
                                          'discount_type',
                                          newType,
                                       )

                                       // Round commission amount to 2 decimals if changing to fixed/flatrate
                                       if (
                                          (newType === 'fixed' ||
                                             newType === 'flatrate') &&
                                          row.commission_discount_amount !==
                                             null
                                       ) {
                                          const rounded =
                                             Math.round(
                                                row.commission_discount_amount *
                                                   100,
                                             ) / 100
                                          if (
                                             rounded !==
                                             row.commission_discount_amount
                                          ) {
                                             handleFieldChange(
                                                row.temp_id,
                                                'commission_discount_amount',
                                                rounded,
                                             )
                                          }
                                       }
                                    }}
                                    required
                                 >
                                    <option value='percent'>Percent</option>
                                    <option value='fixed'>Fixed</option>
                                    <option value='flatrate'>Flat Rate</option>
                                 </Form.Select>
                              </Form.Group>
                           </Col>
                           <Col md={4}>
                              <Form.Group>
                                 <Form.Label>Billing Frequency</Form.Label>
                                 <Form.Select
                                    value={row.billing_freq}
                                    onChange={(e) => {
                                       const newBillingFreq = e.target.value as
                                          | 'monthly'
                                          | 'once'
                                       // Update billing frequency
                                       // Auto-check applies_to_commission for monthly, auto-uncheck for once
                                       const newAppliesToCommission =
                                          newBillingFreq === 'monthly'
                                       setDiscountRows(
                                          discountRows.map((r) => {
                                             if (r.temp_id !== row.temp_id)
                                                return r
                                             // Calculate commission amount when switching to monthly
                                             let newCommissionAmount:
                                                | number
                                                | null = null
                                             if (newAppliesToCommission) {
                                                const discountAmt =
                                                   r.discount_amount === ''
                                                      ? 0
                                                      : (r.discount_amount as number)
                                                newCommissionAmount =
                                                   calculateDefaultCommissionAmount(
                                                      discountAmt,
                                                      r.discount_type,
                                                      r.start_month,
                                                      r.end_month,
                                                   )
                                             }
                                             return {
                                                ...r,
                                                billing_freq: newBillingFreq,
                                                apply_to: new Set<string>(), // Clear apply_to when billing frequency changes
                                                applies_to_commission:
                                                   newAppliesToCommission,
                                                commission_discount_amount:
                                                   newCommissionAmount,
                                             }
                                          }),
                                       )
                                    }}
                                    required
                                 >
                                    <option value='monthly'>Monthly</option>
                                    <option value='once'>One Time</option>
                                 </Form.Select>
                              </Form.Group>
                           </Col>
                           {row.billing_freq === 'monthly' && (
                              <>
                                 <Col md={2}>
                                    <Form.Group>
                                       <Form.Label>Start Month</Form.Label>
                                       <Form.Control
                                          type='number'
                                          value={row.start_month ?? ''}
                                          onChange={(e) => {
                                             const newStartMonth = e.target
                                                .value
                                                ? parseInt(e.target.value)
                                                : null
                                             // Update start month and recalculate commission if applicable
                                             setDiscountRows(
                                                discountRows.map((r) => {
                                                   if (
                                                      r.temp_id !== row.temp_id
                                                   )
                                                      return r
                                                   const updates: Partial<DiscountFormRow> =
                                                      {
                                                         start_month:
                                                            newStartMonth,
                                                      }
                                                   // Recalculate commission amount if applies_to_commission is checked and discount type is percent
                                                   if (
                                                      r.applies_to_commission &&
                                                      r.discount_type ===
                                                         'percent' &&
                                                      r.discount_amount !== ''
                                                   ) {
                                                      updates.commission_discount_amount =
                                                         calculateDefaultCommissionAmount(
                                                            r.discount_amount as number,
                                                            r.discount_type,
                                                            newStartMonth,
                                                            r.end_month,
                                                         )
                                                   }
                                                   return { ...r, ...updates }
                                                }),
                                             )
                                          }}
                                          placeholder='Optional'
                                       />
                                    </Form.Group>
                                 </Col>
                                 <Col md={2}>
                                    <Form.Group>
                                       <Form.Label>End Month</Form.Label>
                                       <Form.Control
                                          type='number'
                                          value={row.end_month ?? ''}
                                          onChange={(e) => {
                                             const newEndMonth = e.target.value
                                                ? parseInt(e.target.value)
                                                : null
                                             // Update end month and recalculate commission if applicable
                                             setDiscountRows(
                                                discountRows.map((r) => {
                                                   if (
                                                      r.temp_id !== row.temp_id
                                                   )
                                                      return r
                                                   const updates: Partial<DiscountFormRow> =
                                                      {
                                                         end_month: newEndMonth,
                                                      }
                                                   // Recalculate commission amount if applies_to_commission is checked and discount type is percent
                                                   if (
                                                      r.applies_to_commission &&
                                                      r.discount_type ===
                                                         'percent' &&
                                                      r.discount_amount !== ''
                                                   ) {
                                                      updates.commission_discount_amount =
                                                         calculateDefaultCommissionAmount(
                                                            r.discount_amount as number,
                                                            r.discount_type,
                                                            r.start_month,
                                                            newEndMonth,
                                                         )
                                                   }
                                                   return { ...r, ...updates }
                                                }),
                                             )
                                          }}
                                          placeholder='Optional'
                                       />
                                    </Form.Group>
                                 </Col>
                              </>
                           )}
                        </Row>

                        <Row className='mb-2'>
                           <Col>
                              <Form.Group>
                                 <Form.Label>Apply To (Products)</Form.Label>
                                 {row.billing_freq === 'once' && (
                                    <div className='mb-2'>
                                       <Description
                                          className='width-text'
                                          content={
                                             <>
                                                {getString(
                                                   'DealDeskDiscountExtendedProductsDescription',
                                                   'Show Additional Products, such as Time Clock Models and Shipping Costs, Additional Implementation Types not currently on the quote, and Professional and Consulting Services',
                                                )}
                                             </>
                                          }
                                          placement='top'
                                       >
                                          <Form.Check
                                             type='checkbox'
                                             id={`show-consulting-${row.temp_id}`}
                                             label='Show extended product list'
                                             checked={
                                                showConsultingServices[
                                                   row.temp_id
                                                ] || false
                                             }
                                             onChange={(e) => {
                                                setShowConsultingServices({
                                                   ...showConsultingServices,
                                                   [row.temp_id]:
                                                      e.target.checked,
                                                })
                                             }}
                                             style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                             }}
                                             className='hoverable'
                                          />
                                       </Description>
                                    </div>
                                 )}
                                 <Dropdown autoClose='outside'>
                                    {invalidRows.has(row.temp_id) && (
                                       <div className='text-danger small mb-1'>
                                          Please select at least one product
                                       </div>
                                    )}
                                    <Dropdown.Toggle
                                       variant={
                                          invalidRows.has(row.temp_id)
                                             ? 'outline-danger'
                                             : 'outline-secondary'
                                       }
                                       id={`dropdown-${row.temp_id}`}
                                       className='w-100 text-start d-flex align-items-center'
                                    >
                                       {row.apply_to.size === 0
                                          ? 'Select products...'
                                          : Array.from(row.apply_to)
                                               .map((code: string) => {
                                                  const product =
                                                     state.product_list[code]
                                                  let displayName =
                                                     product?.ProductName ||
                                                     code

                                                  // Override display name for HR implementation products
                                                  if (
                                                     product?.ProductFamily ===
                                                     'HR implementation'
                                                  ) {
                                                     // Parse the product code to determine implementation type and core level
                                                     let impType = ''
                                                     let coreLevel = ''

                                                     // Determine implementation type from product code
                                                     if (
                                                        code.includes(
                                                           'PremierImp',
                                                        )
                                                     ) {
                                                        impType =
                                                           'Premier Implementation'
                                                     } else if (
                                                        code.includes(
                                                           'Quickstart',
                                                        )
                                                     ) {
                                                        impType = 'QuickStart'
                                                     } else if (
                                                        code.endsWith('Imp')
                                                     ) {
                                                        impType =
                                                           'HR Implementation'
                                                     }

                                                     // Determine core level from product code
                                                     if (
                                                        code.startsWith('core1')
                                                     ) {
                                                        coreLevel = 'Core'
                                                     } else if (
                                                        code.startsWith('core2')
                                                     ) {
                                                        coreLevel = 'Pro'
                                                     } else if (
                                                        code.startsWith('core3')
                                                     ) {
                                                        coreLevel = 'Elite'
                                                     }

                                                     if (impType && coreLevel) {
                                                        displayName = `${impType} - ${coreLevel}`
                                                     }
                                                  }

                                                  // Override display name for Payroll implementation products
                                                  if (
                                                     product?.ProductFamily ===
                                                     'Payroll Implementation'
                                                  ) {
                                                     // Expected formats: payrollImp, payrollFoundationsImp
                                                     if (
                                                        code === 'payrollImp'
                                                     ) {
                                                        displayName =
                                                           'Payroll Implementation - Full'
                                                     } else if (
                                                        code ===
                                                        'payrollFoundationsImp'
                                                     ) {
                                                        displayName =
                                                           'Payroll Implementation - Foundations'
                                                     }
                                                  }

                                                  // Prepend product family name for Consulting Services
                                                  if (
                                                     product?.ProductFamily ===
                                                     'Consulting Services'
                                                  ) {
                                                     displayName = `Consulting Service - ${displayName}`
                                                  }

                                                  // Prepend product family name for Professional Services
                                                  if (
                                                     product?.ProductFamily ===
                                                     'Professional Services'
                                                  ) {
                                                     displayName = `Professional Service - ${displayName}`
                                                  }

                                                  return displayName
                                               })
                                               .join(', ')}
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu
                                       style={{
                                          maxHeight: '250px',
                                          overflowY: 'auto',
                                          width: '100%',
                                       }}
                                    >
                                       {getFilteredProducts(
                                          row.billing_freq,
                                          row.temp_id,
                                       ).map((product) => (
                                          <Dropdown.Item
                                             key={product.code}
                                             onClick={(e) => {
                                                e.stopPropagation()
                                                const newApplyTo = new Set(
                                                   row.apply_to,
                                                )
                                                if (
                                                   newApplyTo.has(product.code)
                                                ) {
                                                   newApplyTo.delete(
                                                      product.code,
                                                   )
                                                } else {
                                                   newApplyTo.add(product.code)
                                                }
                                                handleApplyToChange(
                                                   row.temp_id,
                                                   newApplyTo,
                                                )
                                                // Auto-check extended products if an extended product is selected
                                                const extendedProductFamilies =
                                                   [
                                                      'Consulting Services',
                                                      'Time Clocks',
                                                      'Time Clocks Shipping',
                                                   ]
                                                if (
                                                   row.billing_freq ===
                                                      'once' &&
                                                   newApplyTo.has(
                                                      product.code,
                                                   ) &&
                                                   extendedProductFamilies.includes(
                                                      state.product_list[
                                                         product.code
                                                      ]?.ProductFamily || '',
                                                   )
                                                ) {
                                                   setShowConsultingServices({
                                                      ...showConsultingServices,
                                                      [row.temp_id]: true,
                                                   })
                                                }
                                             }}
                                             active={row.apply_to.has(
                                                product.code,
                                             )}
                                          >
                                             {product.name}
                                          </Dropdown.Item>
                                       ))}
                                    </Dropdown.Menu>
                                 </Dropdown>
                                 <Form.Text className='text-muted'>
                                    Select one or more products (filtered by
                                    billing frequency)
                                 </Form.Text>
                              </Form.Group>
                           </Col>
                        </Row>

                        <Row className='mb-2'>
                           <Col md={6}>
                              <Form.Group>
                                 <Form.Check
                                    type='checkbox'
                                    label='Applies to Commission'
                                    checked={row.applies_to_commission}
                                    onChange={(e) => {
                                       const isChecked = e.target.checked
                                       // Calculate default commission amount when checking
                                       const discountAmt =
                                          row.discount_amount === ''
                                             ? 0
                                             : (row.discount_amount as number)
                                       const newCommissionAmount = isChecked
                                          ? calculateDefaultCommissionAmount(
                                               discountAmt,
                                               row.discount_type,
                                               row.start_month,
                                               row.end_month,
                                            )
                                          : null // Clear when unchecking
                                       setDiscountRows(
                                          discountRows.map((r) =>
                                             r.temp_id === row.temp_id
                                                ? {
                                                     ...r,
                                                     applies_to_commission:
                                                        isChecked,
                                                     commission_discount_amount:
                                                        newCommissionAmount,
                                                  }
                                                : r,
                                          ),
                                       )
                                    }}
                                    style={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: '0.5rem',
                                    }}
                                 />
                              </Form.Group>
                              <Form.Group>
                                 <Form.Check
                                    type='checkbox'
                                    id={`apply-to-pdf-${row.temp_id}`}
                                    label={
                                       <Description
                                          className=''
                                          placement='top'
                                          content='When checked, this discount will be displayed as a line item on the PDF quote.'
                                       >
                                          <span className='hoverable'>
                                             Apply To Line Items
                                          </span>
                                       </Description>
                                    }
                                    checked={row.apply_to_pdf_displayed_value}
                                    onChange={(e) =>
                                       handleFieldChange(
                                          row.temp_id,
                                          'apply_to_pdf_displayed_value',
                                          e.target.checked,
                                       )
                                    }
                                    style={{
                                       display: 'flex',
                                       alignItems: 'center',
                                       gap: '0.5rem',
                                    }}
                                 />
                              </Form.Group>
                           </Col>
                           <Col md={6}>
                              <Form.Group>
                                 <Form.Label>
                                    Commission Discount Amount (%)
                                 </Form.Label>
                                 {invalidCommissionRows.has(row.temp_id) &&
                                    row.applies_to_commission && (
                                       <div className='text-danger small mb-1'>
                                          Commission discount amount must be
                                          greater than 0
                                       </div>
                                    )}
                                 <InputGroup>
                                    <Form.Control
                                       type='number'
                                       step='0.00001'
                                       value={
                                          row.commission_discount_amount ?? ''
                                       }
                                       onChange={(e) => {
                                          if (e.target.value === '') {
                                             handleFieldChange(
                                                row.temp_id,
                                                'commission_discount_amount',
                                                null,
                                             )
                                          } else {
                                             const value = e.target.value
                                             // Commission discount amount is always a percentage, max 5 decimals
                                             const maxDecimals = 5
                                             const decimalMatch =
                                                value.match(/\.\d+$/)
                                             if (
                                                decimalMatch &&
                                                decimalMatch[0].length - 1 >
                                                   maxDecimals
                                             ) {
                                                return // Prevent input if too many decimals
                                             }
                                             handleFieldChange(
                                                row.temp_id,
                                                'commission_discount_amount',
                                                parseFloat(value),
                                             )
                                          }
                                       }}
                                       disabled={!row.applies_to_commission}
                                       placeholder='0'
                                       isInvalid={
                                          invalidCommissionRows.has(
                                             row.temp_id,
                                          ) && row.applies_to_commission
                                       }
                                    />
                                    <InputGroup.Text>%</InputGroup.Text>
                                 </InputGroup>
                              </Form.Group>
                           </Col>
                        </Row>

                        <Row className='mb-2'>
                           <Col>
                              <Form.Group>
                                 <Form.Label>
                                    <Description
                                       className=''
                                       placement='right'
                                       title='Available Keywords'
                                       content={
                                          <>
                                             <code>$DISCOUNT_AMOUNT</code> -
                                             Total discount amount
                                             <br />
                                             <code>$DISCOUNT_PERCENT</code> -
                                             Total discount percentage
                                             <br />
                                             <code>
                                                $TOTAL_WITHOUT_DISCOUNT
                                             </code>{' '}
                                             - Monthly total without this
                                             discount
                                             <br />
                                             <code>
                                                $ONGOING_MONTHLY_TOTAL
                                             </code>{' '}
                                             - Monthly total without any
                                             discounts with an End month
                                             <br />
                                             <code>$START_MONTH</code> -
                                             Discount start month
                                             <br />
                                             <code>$END_MONTH</code> - Discount
                                             end month
                                             <br />
                                             <code>$APPLIED_PRODUCTS</code> -
                                             List of products discount applies
                                             to
                                          </>
                                       }
                                    >
                                       <span className='hoverable'>
                                          Disclaimer
                                       </span>
                                    </Description>
                                 </Form.Label>
                                 <Form.Control
                                    as='textarea'
                                    rows={2}
                                    value={row.disclaimer}
                                    onChange={(e) =>
                                       handleFieldChange(
                                          row.temp_id,
                                          'disclaimer',
                                          e.target.value,
                                       )
                                    }
                                    placeholder='Enter disclaimer text (optional)'
                                 />
                                 <Form.Text className='text-muted'>
                                    This disclaimer will be shown on the quote
                                    for the products this discount applies to.
                                 </Form.Text>
                              </Form.Group>
                           </Col>
                        </Row>
                     </div>
                  ))}
               </Form>
            </Modal.Body>
            <Modal.Footer>
               <Button variant='secondary' onClick={handleClose}>
                  {getString('global.Cancel', 'Cancel')}
               </Button>
               <Button
                  variant='primary'
                  onClick={handleSubmit}
                  disabled={
                     (discountRows.length === 0 &&
                        state.dealDeskDiscountRecords.length === 0) ||
                     invalidRows.size > 0
                  }
               >
                  {getString('global.Save', 'Save')}
               </Button>
            </Modal.Footer>
         </Modal>
      </>
   )
}
